import { Matrix4, Quaternion, Vector3 } from 'three'

export type ArmPlacementItem = {
  matrix?: Matrix4
  catalogID?: string
  width?: number
  morphs?: { name?: string; default?: number; _default?: number }[]
  long?: string
  x?: number
  y?: number
  z?: number
}

/** Ancho efectivo del módulo/brazo (morph "Ancho" o width). */
export const resolveLayoutWidth = (unit: ArmPlacementItem) => {
  const morph = unit.morphs?.find((entry) => entry?.name === 'Ancho' || entry?.name === 'width')
  const longText = String(unit.long || '').toLowerCase()
  const morphDefault = longText.includes('encimera b3200')
    ? morph?._default
    : longText !== 'frontal' || longText.includes('zocalo')
      ? morph?.default
      : morph?._default
  const morphWidth = Number(morphDefault)
  if (Number.isFinite(morphWidth) && morphWidth > 0) return morphWidth

  const itemWidth = Number(unit.width || 0)
  return Number.isFinite(itemWidth) && itemWidth > 0 ? itemWidth : 0
}

const getArmBaseNumber = (catalogID: string) => {
  const match = String(catalogID || '')
    .toUpperCase()
    .match(/^BR(\d+)/)
  return match ? Number(match[1]) : NaN
}

export const isBr12Catalog = (catalogID: string) => /^BR[12](-\d+)?$/i.test(String(catalogID || ''))

/** Lado del brazo respecto al eje X local del módulo. */
export const getArmSideSign = (parentItem: ArmPlacementItem, armItem: ArmPlacementItem): 1 | -1 => {
  if (!parentItem?.matrix || !armItem?.matrix) return 1
  const parentPos = new Vector3()
  const parentQuat = new Quaternion()
  parentItem.matrix.decompose(parentPos, parentQuat, new Vector3())
  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
  const armPos = new Vector3()
  armItem.matrix.decompose(armPos, new Quaternion(), new Vector3())
  return armPos.clone().sub(parentPos).dot(parentXAxis) >= 0 ? 1 : -1
}

/**
 * Posición de aproximación (lejos del módulo) para luego hacer settle/flush.
 * Misma fórmula que ArmsCushionsPanel.placeNewArmNextToParent al dibujar brazos.
 * No usar como posición final: para eso está `buildFlushMatrixForArm`.
 */
export const buildApproachMatrixForArm = (
  parentItem: ArmPlacementItem,
  newArm: ArmPlacementItem,
  sideSign: number,
  extraDistance = 0,
) => {
  if (!parentItem.matrix || !newArm.matrix) return null

  const parentPos = new Vector3()
  const parentQuat = new Quaternion()
  const parentScale = new Vector3()
  parentItem.matrix.decompose(parentPos, parentQuat, parentScale)
  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)

  const armNumber = getArmBaseNumber(String(newArm.catalogID || ''))
  const inwardOffset = armNumber === 1 || armNumber === 2 ? 45 : 0
  const approachDistance = Math.max(
    0,
    resolveLayoutWidth(parentItem) +
      resolveLayoutWidth(newArm) +
      300 +
      Number(extraDistance || 0) -
      inwardOffset,
  )

  const approachPos = parentPos
    .clone()
    .add(parentXAxis.clone().multiplyScalar(sideSign * approachDistance))

  // Heredar rotación del módulo ancla (p. ej. pata de L/U a 90°).
  return new Matrix4().compose(approachPos, parentQuat, parentScale)
}

/**
 * Posición final del brazo a tope con el módulo (flush lateral).
 * Derecha: parentPos + parentWidth; izquierda: parentPos − armWidth.
 * Usa `resolveLayoutWidth` para respetar morphs (p. ej. RELAX/160).
 */
export const buildFlushMatrixForArm = (
  parentItem: ArmPlacementItem,
  armItem: ArmPlacementItem,
  sideSign: 1 | -1,
) => {
  if (!parentItem.matrix || !armItem.matrix) return null

  const parentPos = new Vector3()
  const parentQuat = new Quaternion()
  const parentScale = new Vector3()
  parentItem.matrix.decompose(parentPos, parentQuat, parentScale)
  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)

  const parentWidth = resolveLayoutWidth(parentItem) || Number(parentItem.width || 0)
  const armWidth = resolveLayoutWidth(armItem) || Number(armItem.width || 0)
  if (!(parentWidth > 0) || !(armWidth > 0)) return null

  // Misma convención que el flush histórico de collideItems:
  // derecha → +ancho padre; izquierda → −ancho brazo.
  const widthToUse = sideSign > 0 ? parentWidth : armWidth
  const flushPos = parentPos
    .clone()
    .add(parentXAxis.clone().multiplyScalar(sideSign * widthToUse))
  flushPos.y = parentPos.y

  return new Matrix4().compose(flushPos, parentQuat, parentScale)
}

/** Alinea la rotación del brazo con la del módulo padre, conservando posición. */
export const syncArmRotationToParent = (
  armItem: ArmPlacementItem,
  parentItem: ArmPlacementItem,
) => {
  if (!armItem?.matrix || !parentItem?.matrix) return null

  const armPos = new Vector3()
  const armScale = new Vector3()
  armItem.matrix.decompose(armPos, new Quaternion(), armScale)

  const parentQuat = new Quaternion()
  parentItem.matrix.decompose(new Vector3(), parentQuat, new Vector3())

  return new Matrix4().compose(armPos, parentQuat, armScale)
}

/**
 * Ajuste fino BR1/BR2 tras colisión: 15 mm hacia el centro y -15 en Y.
 */
export const buildBr12FineTunedMatrix = (
  armItem: ArmPlacementItem,
  parentItem: ArmPlacementItem,
  finalSideSign: 1 | -1,
) => {
  if (!armItem.matrix || !parentItem.matrix) return null
  if (!isBr12Catalog(String(armItem.catalogID || ''))) return null

  const armPos = new Vector3()
  const armScale = new Vector3()
  armItem.matrix.decompose(armPos, new Quaternion(), armScale)

  const parentPos = new Vector3()
  const parentQuat = new Quaternion()
  parentItem.matrix.decompose(parentPos, parentQuat, new Vector3())
  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)

  const inwardNudge = 15
  const nudgedPos = armPos
    .clone()
    .add(parentXAxis.clone().multiplyScalar(-finalSideSign * inwardNudge))

  return new Matrix4().compose(nudgedPos, parentQuat, armScale)
}

export const decomposeMatrixPosition = (matrix: Matrix4) => {
  const pos = new Vector3()
  matrix.decompose(pos, new Quaternion(), new Vector3())
  return pos
}
